import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Star, Plus, Minus, ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "@/lib/api";
import { AllergenBadge } from "@/components/AllergenBadge";
import { useCart } from "@/context/CartContext";
import { Loader } from "@/components/Loader";

export default function MealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [meal, setMeal] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [addons, setAddons] = useState([]);
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({ kid_name: "", rating: 5, comment: "" });
  const [submitting, setSubmitting] = useState(false);

  const loadReviews = () =>
    api.get(`/meals/${id}/reviews`).then((r) => setReviews(r.data.reviews || []));

  useEffect(() => {
    setLoading(true);
    setSelected([]);
    Promise.all([api.get(`/meals/${id}`), loadReviews(), api.get(`/clover/meal-addons/${id}`)])
      .then(([m, , a]) => { setMeal(m.data.meal); setAddons(a?.data?.addons || []); })
      .catch(() => toast.error("Could not load this meal."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const toggleAddon = (key) =>
    setSelected((s) => (s.includes(key) ? s.filter((x) => x !== key) : [...s, key]));

  if (loading) return <Loader label="Loading meal…" />;
  if (!meal) return null;

  const submitReview = async (e) => {
    e.preventDefault();
    if (!form.kid_name.trim() || !form.comment.trim()) {
      toast.error("Please add a name and a comment.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/meals/${id}/reviews`, form);
      toast.success("Thanks for the review! ⭐");
      setForm({ kid_name: "", rating: 5, comment: "" });
      await loadReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit review.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <button
        data-testid="back-button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 font-semibold text-slate-500 hover:text-emerald-600"
      >
        <ArrowLeft size={18} /> Back
      </button>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="rounded-3xl overflow-hidden border-2 border-emerald-100 shadow-lg h-[340px] sm:h-[420px]">
          <img src={meal.image_url} alt={meal.name} className="w-full h-full object-cover" />
        </div>

        <div className="flex flex-col">
          <span className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-700 text-sm font-bold px-3 py-1 rounded-full w-fit">
            <Star size={15} className="fill-amber-400 text-amber-400" />
            {(meal.kid_rating || 0).toFixed(1)} · {meal.rating_count || 0} reviews
          </span>
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-slate-800 tracking-tight mt-3">
            {meal.name}
          </h1>
          <p className="text-slate-600 mt-3 leading-relaxed">{meal.description}</p>

          {addons.length > 0 && (
            <div className="mt-5" data-testid="meal-addons">
              <p className="font-display font-semibold text-slate-800 mb-2">Customize your meal</p>
              <div className="flex flex-col gap-2">
                {addons.map((a) => (
                  <label
                    key={a.key}
                    data-testid={`addon-${a.key.replace(/\s+/g, "-")}`}
                    className={`flex items-center justify-between gap-3 rounded-2xl border-2 px-4 py-2.5 cursor-pointer transition-all ${
                      selected.includes(a.key) ? "border-emerald-400 bg-emerald-50" : "border-slate-200"
                    }`}
                  >
                    <span className="flex items-center gap-2 font-medium text-slate-700">
                      <input
                        type="checkbox"
                        checked={selected.includes(a.key)}
                        onChange={() => toggleAddon(a.key)}
                        className="h-4 w-4 accent-emerald-500"
                      />
                      {a.label}
                    </span>
                    <span className="text-sm font-semibold text-emerald-600">+{formatPrice(a.price_cents)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {meal.allergens?.length > 0 && (
            <div className="mt-5">
              <p className="font-display font-semibold text-slate-800 mb-2">Contains:</p>
              <div className="flex flex-wrap gap-2">
                {meal.allergens.map((a) => (
                  <AllergenBadge key={a} allergen={a} />
                ))}
              </div>
            </div>
          )}

          <div className="mt-auto pt-6 flex flex-wrap items-center gap-4">
            <span data-testid="detail-price" className="font-display font-bold text-3xl text-emerald-600">
              {formatPrice(meal.price_cents + selected.reduce((s, k) => s + (addons.find((a) => a.key === k)?.price_cents || 0), 0))}
            </span>
            <div className="flex items-center gap-3 bg-white border-2 border-emerald-100 rounded-2xl p-1.5">
              <button
                data-testid="detail-qty-minus"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="h-9 w-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center hover:bg-emerald-100"
              >
                <Minus size={16} strokeWidth={3} />
              </button>
              <span data-testid="detail-qty-value" className="font-display font-bold text-lg w-6 text-center">
                {qty}
              </span>
              <button
                data-testid="detail-qty-plus"
                onClick={() => setQty((q) => q + 1)}
                className="h-9 w-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center hover:bg-emerald-100"
              >
                <Plus size={16} strokeWidth={3} />
              </button>
            </div>
            <button
              data-testid="detail-add-to-cart"
              onClick={() => {
                addItem(meal, qty, null, selected);
                toast.success(`${qty} × ${meal.name} added!`);
              }}
              className="flex-1 min-w-[160px] inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-display font-bold text-lg rounded-2xl px-6 py-3 shadow-md shadow-emerald-200 transition-all active:scale-[0.98]"
            >
              <Plus size={18} strokeWidth={3} /> Add to Cart
            </button>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <div className="grid lg:grid-cols-3 gap-6 pt-4">
        <div className="lg:col-span-2 space-y-4">
          <h2 className="font-display font-bold text-2xl text-slate-800">What kids say 💬</h2>
          {reviews.length === 0 ? (
            <p className="text-slate-500">No reviews yet. Be the first!</p>
          ) : (
            reviews.map((r) => (
              <div
                key={r.review_id}
                data-testid={`review-${r.review_id}`}
                className="bg-white rounded-3xl border-2 border-emerald-100/80 p-5"
              >
                <div className="flex items-center justify-between">
                  <span className="font-display font-semibold text-slate-800">{r.kid_name}</span>
                  <span className="inline-flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        size={15}
                        className={i < r.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}
                      />
                    ))}
                  </span>
                </div>
                <p className="text-slate-600 mt-2">{r.comment}</p>
              </div>
            ))
          )}
        </div>

        {/* Add review form */}
        <form
          onSubmit={submitReview}
          className="bg-white rounded-3xl border-2 border-emerald-100 p-5 h-fit space-y-4"
        >
          <h3 className="font-display font-bold text-xl text-slate-800">Leave a review</h3>
          <input
            data-testid="review-name-input"
            placeholder="Kid's name"
            value={form.kid_name}
            onChange={(e) => setForm((f) => ({ ...f, kid_name: e.target.value }))}
            className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-emerald-400 outline-none font-medium"
          />
          <div className="flex items-center gap-1.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                data-testid={`review-star-${n}`}
                onClick={() => setForm((f) => ({ ...f, rating: n }))}
              >
                <Star
                  size={28}
                  className={n <= form.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}
                />
              </button>
            ))}
          </div>
          <textarea
            data-testid="review-comment-input"
            placeholder="How was it?"
            rows={3}
            value={form.comment}
            onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
            className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-emerald-400 outline-none font-medium resize-none"
          />
          <button
            type="submit"
            data-testid="review-submit-button"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-rose-400 hover:bg-rose-500 disabled:opacity-60 text-white font-display font-bold rounded-2xl py-3 shadow-md shadow-rose-200 transition-all active:scale-[0.98]"
          >
            <Send size={16} /> {submitting ? "Posting…" : "Post Review"}
          </button>
        </form>
      </div>
    </div>
  );
}
